import { useState } from "react";

import { useT } from "../../shared/i18n";
import { Banner, Button, Dialog, useToast } from "../../shared/ui";
import type { DiscardOutcome } from "./interfaceDraft";

interface InterfaceDraftBarProps {
  discard: () => Promise<DiscardOutcome>;
  /** Called once the draft is gone, to leave the preview. */
  onDiscarded: () => void;
  /** Called when the draft changed under the user; it should be reloaded. */
  onStale?: () => void;
}

/** Banner for an interface change, with a confirmed "discard" action. */
export function InterfaceDraftBar({
  discard,
  onDiscarded,
  onStale,
}: InterfaceDraftBarProps) {
  const t = useT();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);

  const confirm = () => {
    setRunning(true);
    discard()
      .then((outcome) => {
        setOpen(false);
        if (outcome === "stale") {
          toast.show(t("draft.stale"), "error");
          onStale?.();
          return;
        }
        toast.show(t("draft.interface.discarded"), "success");
        onDiscarded();
      })
      .catch((cause: unknown) => {
        toast.show(
          cause instanceof Error ? cause.message : t("draft.interface.failed"),
          "error",
        );
      })
      .finally(() => {
        setRunning(false);
      });
  };

  return (
    <>
      <Banner
        tone="info"
        title={t("draft.interface.title")}
        action={
          <Button
            size="sm"
            variant="tinted"
            onClick={() => {
              setOpen(true);
            }}
          >
            {t("draft.interface.discard")}
          </Button>
        }
      >
        {t("draft.interface.body")}
      </Banner>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!running) setOpen(next);
        }}
        title={t("draft.interface.confirm.title")}
        description={t("draft.interface.confirm.body")}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={running}
              onClick={() => {
                setOpen(false);
              }}
            >
              {t("record.cancel")}
            </Button>
            <Button loading={running} onClick={confirm}>
              {t("draft.interface.discard")}
            </Button>
          </>
        }
      />
    </>
  );
}
